import { useState } from 'react'
import {
  Check,
  Circle,
  Combine,
  FilePlus2,
  GripVertical,
  Link2,
  MousePointer2,
  Plus,
  Scissors,
  Sparkles,
  X,
} from 'lucide-react'
import { CARD_COLUMNS } from '../data'
import { cardColor, charCount, relativeTime, visibleSnippet } from '../lib/utils'

export default function BoardView({
  project,
  selectedCardId,
  onOpenCard,
  onOpenDocument,
  onMoveCard,
  onAddCard,
  onOpenRoundtable,
  selectMode,
  setSelectMode,
  selection,
  setSelection,
  onMergeSelection,
  onSplitSelection,
  onLinkSelection,
}) {
  const [draggingId, setDraggingId] = useState(null)
  const [dragOverColumn, setDragOverColumn] = useState(null)
  const selectedSet = new Set(selection)
  const linkedCount = project.connections.length

  function toggleSelection(cardId) {
    setSelection((current) => current.includes(cardId) ? current.filter((id) => id !== cardId) : [...current, cardId])
  }

  function handleCardClick(card) {
    if (selectMode) toggleSelection(card.id)
    else onOpenCard(card.id)
  }

  function handleDrop(columnId) {
    if (!draggingId) return
    onMoveCard(draggingId, columnId)
    setDraggingId(null)
    setDragOverColumn(null)
  }

  return (
    <div className="board-view">
      <header className="view-head">
        <div className="view-title">
          <div className="eyebrow-row">
            <span className="eyebrow-dot" />
            SCRIVENER BOARD
            <span className="eyebrow-slash">/</span>
            第一稿
          </div>
          <h1>手稿板<span className="ink-dot">.</span></h1>
          <p className="view-subtitle">把场景摊开，看见故事真正的形状。</p>
          <div className="view-meta-row">
            <span><strong>{project.cards.length}</strong> 张卡片</span>
            <i />
            <span><strong>{linkedCount}</strong> 条关联</span>
            <i />
            <span><strong>{project.cards.reduce((sum, card) => sum + charCount(card.content), 0).toLocaleString('zh-CN')}</strong> 字</span>
            <i />
            <span className="muted-meta">上次编辑 {relativeTime(project.updatedAt)}</span>
          </div>
        </div>
        <div className="view-actions">
          <button
            className={'btn btn--outline ' + (selectMode ? 'is-active' : '')}
            onClick={() => { setSelectMode(!selectMode); setSelection([]) }}
          >
            <MousePointer2 size={16} /> {selectMode ? '退出多选' : '多选卡片'}
          </button>
          <button className="btn btn--ink" onClick={() => onAddCard('inspiration')}>
            <Plus size={16} /> 新建卡片
          </button>
        </div>
      </header>

      <div className="board-scroll">
        <div className="board-grid">
          {CARD_COLUMNS.map((column, columnIndex) => {
            const cards = project.cards.filter((card) => card.column === column.id)
            const isOver = dragOverColumn === column.id
            return (
              <section
                key={column.id}
                className={'board-column ' + (isOver ? 'is-dragover ' : '') + 'board-column--' + column.id}
                onDragOver={(event) => { event.preventDefault(); setDragOverColumn(column.id) }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setDragOverColumn(null)
                }}
                onDrop={(event) => { event.preventDefault(); handleDrop(column.id) }}
              >
                <div className="column-head">
                  <div className="column-title">
                    <span className="column-mark" style={{ background: column.color }} />
                    <h2>{column.label}</h2>
                    <span className="column-en">{column.en}</span>
                    <span className="column-count">{String(cards.length).padStart(2, '0')}</span>
                  </div>
                  <button className="icon-btn icon-btn--small" onClick={() => onAddCard(column.id)} aria-label={'在' + column.label + '区新建卡片'}>
                    <Plus size={15} />
                  </button>
                </div>
                <div className="column-rule"><span style={{ width: cards.length ? Math.min(100, 24 + cards.length * 18) + '%' : '8%', background: column.color }} /></div>

                <div className={'column-cards ' + (isOver ? 'drop-active' : '')}>
                  {cards.map((card) => {
                    const index = project.cards.findIndex((item) => item.id === card.id)
                    const links = project.connections.filter((link) => link.source === card.id || link.target === card.id).length
                    const isSelected = selectedSet.has(card.id)
                    return (
                      <article
                        key={card.id}
                        className={[
                          'index-card',
                          selectedCardId === card.id ? 'is-open' : '',
                          isSelected ? 'is-selected' : '',
                          draggingId === card.id ? 'is-dragging' : '',
                        ].join(' ')}
                        style={{ '--card-tint': cardColor(card) }}
                        draggable
                        onDragStart={(event) => {
                          setDraggingId(card.id)
                          event.dataTransfer.effectAllowed = 'move'
                          event.dataTransfer.setData('text/plain', card.id)
                        }}
                        onDragEnd={() => { setDraggingId(null); setDragOverColumn(null) }}
                        onClick={() => handleCardClick(card)}
                        onDoubleClick={() => onOpenDocument(card.id)}
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') handleCardClick(card)
                          if (event.key.toLowerCase() === 'o') onOpenDocument(card.id)
                        }}
                        role="button"
                        aria-label={card.title}
                      >
                        <div className="index-card__top">
                          <span className="index-card__number">{String(index + 1).padStart(2, '0')}</span>
                          <span className="index-card__acted">{card.act?.split(' · ')[0]}</span>
                          {selectMode ? (
                            <button
                              className={'card-check ' + (isSelected ? 'is-checked' : '')}
                              onClick={(event) => { event.stopPropagation(); toggleSelection(card.id) }}
                              aria-label={isSelected ? '取消选择' : '选择卡片'}
                            >
                              {isSelected ? <Check size={12} /> : null}
                            </button>
                          ) : (
                            <span className="card-grip"><GripVertical size={14} /></span>
                          )}
                        </div>
                        <h3>{card.title}</h3>
                        <p>{visibleSnippet(card.content, 82)}</p>
                        <div className="index-card__tags">
                          {(card.tags || []).slice(0, 2).map((tag) => <span key={tag}>{tag}</span>)}
                          {(card.tags || []).length > 2 ? <span>+{(card.tags || []).length - 2}</span> : null}
                        </div>
                        <div className="index-card__foot">
                          <span className={'relation-chip ' + (links ? '' : 'is-empty')}>
                            <Link2 size={12} /> {links ? links + ' 条关联' : '未接线'}
                          </span>
                          <span>{charCount(card.content)} 字</span>
                        </div>
                      </article>
                    )
                  })}

                  {cards.length === 0 ? (
                    <button className="column-empty" onClick={() => onAddCard(column.id)}>
                      <span><FilePlus2 size={17} /></span>
                      <p>这一格还空着</p>
                      <small>拖一张卡片过来，或新建一张</small>
                    </button>
                  ) : null}
                </div>

                <button className="column-add" onClick={() => onAddCard(column.id)}>
                  <Plus size={14} /> 添加卡片
                </button>
              </section>
            )
          })}
        </div>
      </div>

      {selectMode && selection.length === 0 ? (
        <div className="select-tip">
          <Circle size={8} fill="currentColor" /> 点选卡片以选择
          <span>·</span>
          双击任意卡片仍可打开文稿
        </div>
      ) : null}

      {selection.length > 0 ? (
        <div className="selection-dock">
          <div className="selection-dock__count">
            <span>{selection.length}</span>
            <p>{selection.length === 1 ? '张卡片已选' : '张卡片已选'}</p>
          </div>
          <div className="selection-dock__divider" />
          {selection.length === 1 ? (
            <button onClick={onSplitSelection}><Scissors size={15} /> 拆分卡片</button>
          ) : (
            <button onClick={onMergeSelection}><Combine size={15} /> 合并卡片</button>
          )}
          <button onClick={onLinkSelection} disabled={selection.length !== 2}><Link2 size={15} /> 建立关联</button>
          <button className="selection-dock__ai" onClick={() => onOpenRoundtable(selection)}><Sparkles size={15} /> 带去圆桌</button>
          <button className="selection-dock__clear" onClick={() => setSelection([])} aria-label="清空选择"><X size={15} /></button>
        </div>
      ) : null}
    </div>
  )
}
